import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const getButtonName = (data) => {
  if (data) {
    if (data?.data?.find((item) => item.fields.Name === "Texting")) {
      return "Texting";
    } else if (data?.data?.find((item) => item.fields.Name === "Training")) {
      return "Training";
    } else {
      return "Tasks";
    }
  }
};
const AVG_REVENUE_CACHE_KEY = "avgDailyRevenueCache";
const AVG_REVENUE_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
export const writeAvgRevenueCache = (userId, year, value) => {
  try {
    localStorage.setItem(
      AVG_REVENUE_CACHE_KEY,
      JSON.stringify({ value: value ?? 0, userId, year, cachedAt: Date.now() })
    );
  } catch {
    // localStorage full / disabled — ignore
  }
};

export const readAvgRevenueCache = (userId, year) => {
  try {
    const raw = localStorage.getItem(AVG_REVENUE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.userId !== userId || parsed?.year !== year) return null;
    if (Date.now() - (parsed?.cachedAt ?? 0) > AVG_REVENUE_CACHE_TTL_MS) return null;
    return typeof parsed?.value === "number" ? parsed.value : null;
  } catch {
    return null;
  }
};
// Per-user "landing page" preference — the menu page the user pinned (menu
// drawer → Edit → pin icon) to open to on each fresh login. Stored per-device
// like menu order/colors, and preserved across logout via the same allow-list
// in ButtonsForMobile.handleLogout. The value is the button's canonical Name
// string — the same key navbarButtonsHandler / setContentState route on.
const LANDING_PAGE_PREFIX = "kindred:landingPage";
export const landingPageKey = (userKey) => `${LANDING_PAGE_PREFIX}:${userKey || "anon"}`;

// Per-user menu order ({array of button Names}) and per-button colors
// ({ buttonName: colorKey }). Centralized here (rather than living only in
// ButtonsForMobile) so the server-prefs hydration (lib/menuPrefs.js) re-seeds
// localStorage under the exact same keys the drawer reads — one source of truth.
const MENU_ORDER_PREFIX = "kindred:menuOrder";
export const menuOrderKey = (userKey) => `${MENU_ORDER_PREFIX}:${userKey || "anon"}`;

const MENU_COLORS_PREFIX = "kindred:menuColors";
export const menuColorsKey = (userKey) => `${MENU_COLORS_PREFIX}:${userKey || "anon"}`;

// Per-user Action (⚡ pop-out) drawer order + per-action colors, same convention
// as the menu drawer but namespaced so the two drawers never collide (an action
// and a menu button can share a Name, e.g. "Create Writeup"). Server-prefs
// hydration seeds these under the same keys (lib/menuPrefs.js).
const ACTION_ORDER_PREFIX = "kindred:actionOrder";
export const actionOrderKey = (userKey) => `${ACTION_ORDER_PREFIX}:${userKey || "anon"}`;

const ACTION_COLORS_PREFIX = "kindred:actionColors";
export const actionColorsKey = (userKey) => `${ACTION_COLORS_PREFIX}:${userKey || "anon"}`;

export const readLandingPage = (userKey) => {
  try {
    const v = localStorage.getItem(landingPageKey(userKey));
    return typeof v === "string" && v ? v : null;
  } catch {
    return null;
  }
};

export const writeLandingPage = (userKey, name) => {
  try {
    if (name) localStorage.setItem(landingPageKey(userKey), name);
    else localStorage.removeItem(landingPageKey(userKey));
  } catch {
    /* quota / private mode — ignore */
  }
};

// "Just logged in" handshake between auth and Home's landing-page resolver.
// Set positively at the moment of a successful login (password or magic link);
// consumed (read-and-cleared) once by Home. It is the signal that the pinned
// landing page should override the cached tab. A page refresh does NOT set it,
// so Home keeps you on the exact tab/scroll you were on. Lives in
// sessionStorage so it dies on tab close and is wiped by logout's clear.
// We use an explicit flag rather than "is sessionStorage.contentState empty?"
// because that heuristic mis-fired before (a stale cached tab on login could
// shadow the pin), so logins are detected positively, not by inference.
const JUST_LOGGED_IN_KEY = "kindred:justLoggedIn";

export const markJustLoggedIn = () => {
  try {
    sessionStorage.setItem(JUST_LOGGED_IN_KEY, "1");
  } catch {
    /* private mode / quota — ignore */
  }
};

export const consumeJustLoggedIn = () => {
  try {
    const v = sessionStorage.getItem(JUST_LOGGED_IN_KEY);
    if (v) sessionStorage.removeItem(JUST_LOGGED_IN_KEY);
    return v === "1";
  } catch {
    return false;
  }
};

// "Just deep-linked" handshake between SectionDeepLink and Home's landing-page
// resolver. Set by SectionDeepLink before it navigates to /home so that Home
// knows to skip the pinned-page redirect: the notification/deep-link target was
// already dispatched and must not be overridden by the pin. Consumed (read-and-
// cleared) once by Home's resolveLanding effect. Lives in sessionStorage so it
// dies on tab close.
const JUST_DEEP_LINKED_KEY = "kindred:justDeepLinked";

export const markJustDeepLinked = () => {
  try {
    sessionStorage.setItem(JUST_DEEP_LINKED_KEY, "1");
  } catch {
    /* private mode / quota — ignore */
  }
};

export const consumeJustDeepLinked = () => {
  try {
    const v = sessionStorage.getItem(JUST_DEEP_LINKED_KEY);
    if (v) sessionStorage.removeItem(JUST_DEEP_LINKED_KEY);
    return v === "1";
  } catch {
    return false;
  }
};

// Per-device UI preferences that must SURVIVE logout / session expiry. These
// are user customizations — menu order/colors/pinned landing page, ACTION
// drawer order/colors, draggable menu & action button positions,
// sales-recording tag filters, payroll-admin table prefs, dismissed feature
// banners — NOT auth state. Several logout paths (App.js session-expiry,
// authSlice.logout, the desktop & mobile logout buttons) each hand-maintained
// their own preserve list and they drifted: the menuOrder / menuColors /
// landingPage keys were added to some lists but not others, so a session expiry
// silently wiped a user's menu customization. This is the single source of
// truth — add a new preference key here ONCE.
const isPreservedPrefKey = (key) =>
  typeof key === "string" &&
  (key.startsWith("salesRecordingSelectedTags_") ||
    key.startsWith("kindred:menuButtonPos:") ||
    key.startsWith("kindred:actionButtonPos:") ||
    key.startsWith("kindred:menuOrder:") ||
    key.startsWith("kindred:menuColors:") ||
    key.startsWith("kindred:actionOrder:") ||
    key.startsWith("kindred:actionColors:") ||
    key.startsWith("kindred:landingPage:") ||
    key.startsWith("kindred:manualReader:") ||
    key.startsWith("payrollAdmin") ||
    key === "dismissedFeatureUpdates");

// Clear all localStorage + sessionStorage on logout EXCEPT the per-device UI
// preferences above. Use this everywhere instead of a bare localStorage.clear()
// so a newly added preference key can never be dropped by one logout path while
// surviving another.
export const clearStoragePreservingPrefs = () => {
  try {
    const preserved = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (isPreservedPrefKey(key)) preserved[key] = localStorage.getItem(key);
    }
    localStorage.clear();
    sessionStorage.clear();
    Object.entries(preserved).forEach(([key, value]) => {
      if (value != null) localStorage.setItem(key, value);
    });
  } catch {
    /* private mode / quota — ignore */
  }
};

export const sanitizeFileName = (fileName) => {
  return fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
}

export function formatDateTime(isoString) {
  const date = new Date(isoString);

  return date
    .toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(",", "");
}

export const maskPhoneNumber = (phone) => {
  if (!phone) return "";
  return phone.length > 6 ? "******" + phone.slice(6) : "******";
};

/**
 * Returns whether Proof of Completion is required based on Airtable field value.
 * Airtable may return boolean (true/false) or string ("Yes"/"No") from Task Type lookup,
 * sometimes as an array (e.g. ["Yes"]) or as a single value.
 * Only explicit "yes" or true is treated as required; "No", false, or missing = not required.
 */
export function isProofOfCompletionRequired(fieldValue) {
  const v = Array.isArray(fieldValue) ? fieldValue[0] : fieldValue;
  if (v === true) return true;
  if (v === false || v == null || v === "") return false;
  const s = String(v).trim().toLowerCase();
  if (s === "yes" || s === "true") return true;
  if (s === "no" || s === "false") return false;
  return false;
}

// Helper function to split 'Sub Button' into name and seq, trim them
// Helper function to split 'Sub Button' into name and seq, trim them
export const splitAndTrimSubButton = (text) => {
  if (text === "") return { name: "Other", seq: 999 };
  if (typeof text !== "string") return { name: "", seq: 999 }; // Default values for invalid input
  const [name, seq] = text.includes("-")
    ? text.split("-").map((s) => s.trim())
    : [text, 999];
  return { name, seq: Number(seq) || 999 }; // Default sequence if seq is not a valid number
};

export function getButtons(tasks, userInfo, manuelPDFs) {
  // const isSales = userInfo?.Team === "Sales";
  let buttons = [];

  tasks?.forEach((task) => {
    const { name: buttonName, seq } = splitAndTrimSubButton(
      task["Sub Button (from Task Type)"]
    );

    // Avoid duplicates
    if (!buttons.find((btn) => btn.name === buttonName)) {
      buttons.push({ name: buttonName, seq });
    }
  });

  // if (isSales) {
  //   buttons = [
  //     { name: "Pitch", seq: 1 },
  //     { name: "Launch Pad", seq: 2 },
  //     { name: "Pro Tips", seq: 3 },
  //     { name: "Manual", seq: 4 },
  //     { name: "Sales Recordings", seq: 5 },
  //     { name: "Scavenger Hunt", seq: 6 },
  //   ];
  // }
  // if (manuelPDFs.length > 0 && !isSales) {
  //   buttons.push({ name: "Manual", seq: 999 });
  // }

  // Sort buttons by seq and alphabetically for ties
  buttons.sort((a, b) => {
    if (a.seq === b.seq) return a.name.localeCompare(b.name);
    return a.seq - b.seq;
  });

  return buttons;
}

export const getAvailableTabs = (organizedTasks, isSales, manuelPDFs) => {
  if (!organizedTasks?.length) return [];

  // if (isSales) {
  //   return [
  //     { name: "Pitch", seq: 1 },
  //     { name: "Launch Pad", seq: 2 },
  //     { name: "Pro Tips", seq: 3 },
  //     { name: "Manual", seq: 4 },
  //     { name: "Sales Recordings", seq: 5 },
  //     { name: "Scavenger Hunt", seq: 6 },
  //   ];
  // }

  let buttons = organizedTasks.map((task) => ({
    name: task.name,
    seq: parseInt(task.seq) || 999,
  }));

  // if (manuelPDFs?.length > 0 && !isSales) {
  //   buttons.push({ name: "Manual", seq: 999 });
  // }

  buttons.sort((a, b) => {
    if (a.seq === b.seq) return a.name.localeCompare(b.name);
    return a.seq - b.seq;
  });

  return buttons;
};

export function organizeTasksBySubButton(tasks) {
  const result = [];

  tasks?.forEach((task) => {
    const { name, seq } = splitAndTrimSubButton(
      task["Sub Button (from Task Type)"]
    );

    let mainCategory = result.find((item) => item.name === name);

    if (!mainCategory) {
      mainCategory = { name, seq, taskNumber: task["Task Number"], type: "", value: "", difficulty: [] };
      result.push(mainCategory);
    }

    const { name: difficultyName, seq: difficultySeq } = splitAndTrimSubButton(
      task["Difficulty (from Task Type)"]
    );

    let difficultyCategory = mainCategory.difficulty.find(
      (d) => d.name === difficultyName
    );

    if (!difficultyCategory) {
      difficultyCategory = {
        name: difficultyName,
        seq: difficultySeq,
        subCategory: [],
      };
      mainCategory.difficulty.push(difficultyCategory);
    }

    const rawSubCategoryName = task["Training Sub Category"]
      ? task["Training Sub Category"][0]
      : "Other";

    // Split and trim the subcategory name
    const { name: subCategoryName, seq: subCategorySeq } =
      splitAndTrimSubButton(rawSubCategoryName);

    let subCategory = difficultyCategory.subCategory.find(
      (sub) => sub.name === subCategoryName
    );

    if (!subCategory) {
      subCategory = { name: subCategoryName, seq: subCategorySeq, tasks: [] };
      difficultyCategory.subCategory.push(subCategory);
    }

    subCategory.tasks.push(task);
  });

  // Sorting logic for main category, difficulties, and subcategories
  result.forEach((main) => {
    main.difficulty.sort((a, b) => {
      if (a.name === "Other") return 1; // Place "Other" last
      if (b.name === "Other") return -1; // Place "Other" last
      if (a.seq === b.seq) {
        return a.name.localeCompare(b.name); // Sort alphabetically by name if seq is the same
      }
      return a.seq - b.seq; // Otherwise, sort by seq
    });

    main.difficulty.forEach((difficulty) => {
      difficulty.subCategory.sort((a, b) => {
        if (a.name === "Other") return 1; // Place "Other" last
        if (b.name === "Other") return -1; // Place "Other" last
        if (a.seq === b.seq) {
          return a.name.localeCompare(b.name);
        }
        return a.seq - b.seq;
      });
    });
  });

  return result;
}

export function formatAmericanDate(date) {
  const d = new Date(date);
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  const year = d.getFullYear().toString().slice(-2);
  return `${month}/${day}/${year}`;
}

// For date-only values (e.g. a MySQL DATE column serialized as
// "2026-06-08T00:00:00.000Z"): read UTC components so negative-offset zones
// (US Central) don't roll the calendar date back a day. Local getters would.
export function formatAmericanDateUTC(date) {
  const d = new Date(date);
  const month = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = d.getUTCDate().toString().padStart(2, "0");
  const year = d.getUTCFullYear().toString().slice(-2);
  return `${month}/${day}/${year}`;
}

export const formatStringAmericanDate = (dateString) => {
  // A missing or unparseable value shows a dash instead of "NaN/NaN/NaN".
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

export function formatToCurrency(value) {
  // Convert the input to a number
  const numericValue = typeof value === "string" ? parseFloat(value) : value;

  // Validate if the value is a number
  if (isNaN(numericValue)) {
    console.log("Invalid input: must be a number or a numeric string");
  }

  // Format to currency
  return `$${numericValue?.toFixed(2)}`;
}

export function getAmericanTime() {
  const date = new Date();
  const options = {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false, // 24-hour format
    timeZone: "America/New_York", // Adjust according to the desired time zone
  };

  return date.toLocaleTimeString("en-US", options);
}

console.log(getAmericanTime());

export const formatPaidOnDate = (dateString) => {
  const paidOnDateString = dateString.split("Paid On: ")[1];
  if (!paidOnDateString) return "";
  const paidOnDate = new Date(paidOnDateString);
  const formattedDate = paidOnDate.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  return formattedDate;
};


export function flattenTruckData(apiResponse = []) {
  return apiResponse.map((truck) => {
    const fields = truck.fields || {};

    // Extract and format fields
    const formattedMiles =
      typeof fields["Miles from Samsara"] === "number"
        ? parseFloat(fields["Miles from Samsara"].toFixed(2))
        : undefined;

    const insuranceCardUrl = Array.isArray(fields["Insurance Card"]) && fields["Insurance Card"].length > 0
      ? fields["Insurance Card"][0]?.url
      : null;

    const registrationProofUrl = Array.isArray(fields["Truck Registration Proof"]) && fields["Truck Registration Proof"].length > 0
      ? fields["Truck Registration Proof"][0]?.url
      : null;
    return {
      id: truck.id,
      recid: truck.id,
      ...fields,
      "Miles from Samsara": formattedMiles,
      "Insurance Card": insuranceCardUrl
        ? { label: "View Insurance", url: insuranceCardUrl }
        : null,
      "Truck Registration Proof": registrationProofUrl
        ? { label: "View Registration", url: registrationProofUrl }
        : null,
    };
  });
}


export function inferColumnTruckType(value) {
  // Check for arrays of files or links
  if (Array.isArray(value)) {
    const firstItem = value[0];

    if (typeof firstItem === 'object' && firstItem !== null) {
      if ('url' in firstItem) return 'file'; // Airtable file field
      if ('label' in firstItem && 'url' in firstItem) return 'link'; // Generic link object
    }

    return 'array';
  }

  if (typeof value === 'object' && value !== null) {
    if ('url' in value && 'label' in value) return 'link';
    return 'object';
  }

  if (typeof value === 'number') return 'number';

  if (typeof value === 'string') {
    // Check if it's a valid date string
    const parsedDate = Date.parse(value);
    if (!isNaN(parsedDate)) return 'date';

    // Check if it's a URL
    try {
      new URL(value);
      return 'url';
    } catch (_) { }

    return 'text';
  }

  return 'unknown';
}

export function generateAutoHeaders(data = []) {
  const allKeys = new Set();

  data.forEach(item => {
    Object.keys(item || {}).forEach(key => {
      allKeys.add(key);
    });
  });


  return Array.from(allKeys).map((key) => ({
    header: key,
    field: key,
    type: inferColumnTruckType(getFirstNonNullValue(data, key)),
  }));
}


function getFirstNonNullValue(data, key) {
  for (const item of data) {
    if (item && item[key] !== null && item[key] !== undefined) {
      return item[key];
    }
  }
  return null;
}

export function sortByFieldWithPriority(data = [], key = '', priorityOrder = []) {
  if (!key || !priorityOrder.length) return data;

  const priorityMap = priorityOrder.reduce((acc, value, index) => {
    acc[value.toLowerCase()] = index;
    return acc;
  }, {});

  return [...data].sort((a, b) => {
    const valA = a[key]?.toString().toLowerCase() || '';
    const valB = b[key]?.toString().toLowerCase() || '';

    const rankA = priorityMap.hasOwnProperty(valA) ? priorityMap[valA] : Infinity;
    const rankB = priorityMap.hasOwnProperty(valB) ? priorityMap[valB] : Infinity;

    return rankA - rankB;
  });
}

export const UPLOAD_CATEGORY = {
  PROOF: 'proof-of-completion',
  EMPLOYEE_DEDUCTION: 'employee-deduction',
  SALES_RECORDING: 'sales-recording',
  INVENTORY_INSPECTION: 'inventory-inspection',
  TECH_CERTIFICATE: 'technician-certificate',
  TASK: 'task',
  QUALITY_INSPECTION: 'quality-inspection',
  MASS_MESSAGE: 'mass-message',
  VISION_BOARD: 'vision-board',
};